import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Search, Map, DollarSign, Truck, LogOut, User, UserCircle, Settings, Shield, TrendingUp, Building2, Navigation, Moon, Sun, CreditCard, Bot } from 'lucide-react';
import SearchLoadsUnified from './components/SearchLoadsUnified';
import MapView from './components/MapView';
import LanePricing from './components/LanePricing';
import RouteOptimizationUnified from './components/RouteOptimizationUnified';
import UserProfile from './components/UserProfile';
import AdminPanel from './components/AdminPanel';
import RateInsights from './components/RateInsights';
import BrokerMetrics from './components/BrokerMetrics';
import PricingPlans from './components/PricingPlans';
import FreightAI from './components/FreightAI';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { DataProvider } from './contexts/DataContext';
import { UserProvider, useUser } from './contexts/UserContext';
import sessionLogger from './services/sessionLogger';
import './utils/checkUserRole'; // Load role checker utility

// All available tabs with their role requirements
const allTabs = [
  { id: 'search', name: 'Search Loads', icon: Search, component: SearchLoadsUnified, roles: ['Basic', 'Standard', 'Premium', 'Admin', 'Dev'] },
  { id: 'lane-pricing', name: 'Lane Pricing', icon: DollarSign, component: LanePricing, roles: ['Basic', 'Standard', 'Premium', 'Admin', 'Dev'] },
  { id: 'route-optimization', name: 'Route Optimization', icon: Navigation, component: RouteOptimizationUnified, roles: ['Basic', 'Standard', 'Premium', 'Admin', 'Dev'] },
  { id: 'rate-insights', name: 'Rate Insights', icon: TrendingUp, component: RateInsights, roles: ['Basic', 'Standard', 'Premium', 'Admin', 'Dev'] },
  { id: 'broker-metrics', name: 'Broker Metrics', icon: Building2, component: BrokerMetrics, roles: ['Basic', 'Standard', 'Premium', 'Admin', 'Dev'] },
  { id: 'map', name: 'Heat Map', icon: Map, component: MapView, roles: ['Basic', 'Standard', 'Premium', 'Admin', 'Dev'] },
  { id: 'freight-ai', name: 'Freight AI', icon: Bot, component: FreightAI, roles: ['Basic', 'Standard', 'Premium', 'Admin', 'Dev'] },
  { id: 'upgrade', name: 'Upgrade', icon: CreditCard, component: PricingPlans, roles: ['Basic', 'Standard', 'Premium', 'Admin', 'Dev'] },
  { id: 'admin', name: 'Admin', icon: Shield, component: AdminPanel, roles: ['Admin', 'Dev'] },
  { id: 'profile', name: 'Profile', icon: UserCircle, component: UserProfile, roles: ['Basic', 'Standard', 'Premium', 'Admin', 'Dev'], hidden: true },
];

// Main App Content Component (needs to be inside UserProvider)
function AppContent() {
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('activeTab') || 'search';
  });
  const { user, isLoading, isAuthenticated, loginWithRedirect, logout } = useAuth0();
  const { userRole, loading: userLoading } = useUser();
  const { isDarkMode, toggleTheme } = useTheme();

  useEffect(() => {
    localStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  // Filter tabs based on user role and exclude hidden tabs from navigation
  const availableTabs = allTabs.filter(tab => tab.roles.includes(userRole) && !tab.hidden);
  const allAvailableTabs = allTabs.filter(tab => tab.roles.includes(userRole));
  
  // Debug logging
  console.log(`[App] User role: ${userRole}, Available tabs:`, availableTabs.map(t => t.name));

  // Ensure active tab is available to user, fallback to first available
  useEffect(() => {
    // Don't change tabs while user data is still loading
    if (userLoading) {
      return;
    }

    if (allAvailableTabs.length > 0) {
      const currentTabAvailable = allAvailableTabs.some(tab => tab.id === activeTab);
      
      if (!currentTabAvailable) {
        // Only change tab if current tab is not available to user
        // Try to preserve the saved tab from localStorage first
        const savedTab = localStorage.getItem('activeTab');
        const savedTabAvailable = allAvailableTabs.some(tab => tab.id === savedTab);
        
        if (savedTabAvailable && !allAvailableTabs.find(tab => tab.id === savedTab)?.hidden) {
          setActiveTab(savedTab);
        } else {
          setActiveTab(availableTabs[0].id);
        }
      }
    }
  }, [userRole, availableTabs, userLoading, activeTab]);

  const handleLogout = () => {
    // Clear session logged flag to allow logging on next authentication
    sessionLogger.clearSessionLogged();

    // Clear user data on logout
    localStorage.removeItem('access_token');
    
    // Clean up Auth0 cache to prevent slow logins on next session
    try {
      const auth0Keys = Object.keys(localStorage).filter(key => 
        key.includes('@@auth0spajs@@') || key.includes('auth0')
      );
      auth0Keys.forEach(key => {
        localStorage.removeItem(key);
        console.log(`[App] Cleared Auth0 cache: ${key}`);
      });
    } catch (e) {
      console.warn('[App] Error clearing Auth0 cache on logout:', e);
    }
    
    // For development, use local logout to avoid Auth0 configuration issues
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      logout({ 
        openUrl: false // Just clear local session without redirecting to Auth0
      });
    } else {
      // Production logout with proper return URL
      logout({ 
        logoutParams: {
          returnTo: window.location.origin
        }
      });
    }
  };

  const ActiveComponent = allAvailableTabs.find(tab => tab.id === activeTab)?.component;

  // Debug loading states
  console.log(`[App] Loading states - Auth0 isLoading: ${isLoading}, UserContext userLoading: ${userLoading}`);
  
  // Only wait for Auth0 authentication check, not user data loading
  // User data can load in background while app is usable
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl shadow-lg mb-4">
            <Truck className="w-8 h-8 text-white" />
          </div>
          <div className="w-8 h-8 border-2 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-gray-600">Authenticating...</p>
        </div>
      </div>
    );
  }

  // Show login screen if user is not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl shadow-lg mb-4">
            <Truck className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Lot Wizard</h1>
          <p className="text-gray-600 mb-8">Sign in to access your freight management platform</p>
          <button
            onClick={() => loginWithRedirect()}
            className="w-full bg-gradient-to-r from-primary-500 to-primary-600 text-white py-3 px-4 rounded-lg font-medium hover:from-primary-600 hover:to-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
          >
            Sign In with Auth0
          </button>
          <div className="text-center mt-8">
            <p className="text-sm text-gray-500">
              © 2025 Lot Wizard. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        {/* Header */}
        <header className="bg-white dark:bg-gray-800 shadow-soft border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between min-h-16 py-2 gap-2">
              <div className="flex items-center space-x-3 min-w-0">
                <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-gradient-to-r from-primary-500 to-primary-600 rounded-lg">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <div className="min-w-0">
                  <h1 className="text-xl font-bold text-gray-900 dark:text-white truncate">Lot Wizard</h1>
                  <p className="hidden sm:block text-sm text-gray-500 dark:text-gray-400 truncate">Freight Management Platform</p>
                </div>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
                <button
                  onClick={() => setActiveTab('profile')}
                  className="flex items-center space-x-2 px-2 sm:px-3 py-1 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                  title="View Profile"
                >
                  <User className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  <span className="hidden md:inline text-sm text-gray-700 dark:text-gray-300 max-w-[160px] truncate">
                    {user?.name || user?.email || 'User'}
                  </span>
                </button>
                <button
                  onClick={toggleTheme}
                  className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                  title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  {isDarkMode ? (
                    <Sun className="w-4 h-4 text-yellow-500" />
                  ) : (
                    <Moon className="w-4 h-4 text-gray-600" />
                  )}
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center space-x-1 px-2 sm:px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Sign Out"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Navigation Tabs */}
        <nav className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <div className="flex space-x-6 sm:space-x-8 overflow-x-auto scrollbar-hide flex-nowrap">
                {availableTabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex-shrink-0 flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors duration-200 ${
                        activeTab === tab.id
                          ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                          : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="relative">
                        {tab.name}
                        {(tab.id === 'freight-ai' || tab.id === 'route-optimization' || tab.id === 'broker-metrics') && (
                          <span className="absolute -top-3 -right-9 px-1 py-0.5 text-[9px] font-bold bg-blue-500 text-white rounded uppercase tracking-tight">
                            BETA
                          </span>
                        )}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* User Role Badge */}
              <div className="flex items-center space-x-3 flex-shrink-0 pl-3">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  userRole === 'Admin' ? 'bg-red-100 text-red-800' :
                  userRole === 'Dev' ? 'bg-purple-100 text-purple-800' :
                  userRole === 'Premium' ? 'bg-yellow-100 text-yellow-800' :
                  userRole === 'Standard' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {userRole}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {ActiveComponent && <ActiveComponent />}
          {/* Show message if tab has no component */}
          {!ActiveComponent && (
            <div className="text-center py-12">
              <div className="text-gray-500 dark:text-gray-400">
                <Settings className="w-12 h-12 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Coming Soon</h3>
                <p>This feature is currently under development.</p>
              </div>
            </div>
          )}
        </main>
        
        </div>
  );
}

// Main App wrapper with providers
function App() {
  return (
    <ThemeProvider>
      <DataProvider>
        <UserProvider>
          <AppContent />
        </UserProvider>
      </DataProvider>
    </ThemeProvider>
  );
}

export default App;